/**
 * PlaylistResultsTable Component
 *
 * Displays search results in a table format with header, body, and pagination.
 * Handles loading, error, empty, and success states.
 */

import { Component, For, Switch, Match, Show } from "solid-js";
import type { MetadataHistoryEntry } from "~/types/metadataHistory";
import type { PaginationInfo } from "./Pagination";
import PlaylistResultRow from "./PlaylistResultRow";
import Pagination from "./Pagination";
import { env } from "~/config/env";
import styles from "./PlaylistResultsTable.module.css";

export interface PlaylistResultsTableProps {
  /** Title to display in the results header */
  resultTitle: string;
  /** Whether initial search is loading (no previous results) */
  isLoading: boolean;
  /** Whether search is refetching (has previous results to show) */
  isRefetching?: boolean;
  /** Whether there was an error */
  hasError: boolean;
  /** Error message to display */
  errorMessage: string | null;
  /** Search result entries */
  entries: MetadataHistoryEntry[];
  /** Pagination info (null if not paginated) */
  pagination: PaginationInfo | null;
  /** Callback when retry is clicked */
  onRetry: () => void;
  /** Callback to go to previous page */
  onPreviousPage: () => void;
  /** Callback to go to next page */
  onNextPage: () => void;
}

/**
 * Table header row with column labels
 */
const TableHeader: Component = () => (
  <div class={styles.tableHeader}>
    <span class={styles.headerDate}>Datum</span>
    <span class={styles.headerTime}>Uhrzeit</span>
    <span class={styles.headerTitle}>Titel</span>
    <span class={styles.headerArtist}>Interpret</span>
  </div>
);

/**
 * Loading state display
 */
const LoadingState: Component = () => <div class={styles.loading}>Suche läuft...</div>;

/**
 * Error state with retry button
 */
const ErrorState: Component<{ message: string | null; onRetry: () => void }> = (props) => (
  <div class={styles.error}>
    <span>Suche konnte nicht durchgeführt werden.</span>
    <Show when={env.isDev && props.message}>
      <span class={styles.errorMessage}>{props.message}</span>
    </Show>
    <button class={styles.retryButton} onClick={props.onRetry}>
      Erneut versuchen
    </button>
  </div>
);

/**
 * Empty results state
 */
const EmptyState: Component = () => (
  <div class={styles.noResults}>Keine Ergebnisse für deine Suche gefunden.</div>
);

const PlaylistResultsTable: Component<PlaylistResultsTableProps> = (props) => {
  const hasEntries = () => props.entries.length > 0;

  return (
    <section class={styles.resultsSection}>
      <div class={styles.resultsContainer}>
        <div class={styles.resultsHeader}>
          <span class={styles.resultsLabel}>Deine Suche</span>
          <span class={styles.resultsTitle}>{props.resultTitle}</span>
        </div>

        <div class={styles.resultsTable}>
          <TableHeader />

          <div
            class={styles.tableBody}
            classList={{ [styles.tableBodyRefetching]: props.isRefetching }}
          >
            {/* Loading overlay for refetch - shows over existing content */}
            <Show when={props.isRefetching}>
              <div class={styles.refetchingOverlay}>
                <span class={styles.refetchingText}>Aktualisiere...</span>
              </div>
            </Show>

            <Switch>
              <Match when={props.isLoading}>
                <LoadingState />
              </Match>

              <Match when={props.hasError && !props.isRefetching}>
                <ErrorState message={props.errorMessage} onRetry={props.onRetry} />
              </Match>

              <Match when={!hasEntries() && !props.hasError && !props.isRefetching}>
                <EmptyState />
              </Match>

              <Match when={hasEntries()}>
                <For each={props.entries}>
                  {(entry, index) => (
                    <PlaylistResultRow entry={entry} isAlternate={index() % 2 === 0} />
                  )}
                </For>
              </Match>
            </Switch>
          </div>

          <Show when={props.pagination}>
            <Pagination
              pagination={props.pagination!}
              onPrevious={props.onPreviousPage}
              onNext={props.onNextPage}
            />
          </Show>
        </div>
      </div>
    </section>
  );
};

export default PlaylistResultsTable;
